import React, { useRef, useState, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Html, Environment, PerspectiveCamera, OrbitControls } from "@react-three/drei";
import * as THREE from "three";

const colors = ["#eab308", "#0ea5e9", "#22c55e", "#ef4444", "#a855f7", "#ec4899", "#f97316"];

const Keycap = ({ position, skill, color, onSelect }) => {
  const group = useRef();
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  // Target Y position for press effect
  const targetY = clicked ? position[1] - 0.25 : position[1];

  useFrame(() => {
    if (group.current) {
      group.current.position.y = THREE.MathUtils.lerp(group.current.position.y, targetY, 0.4);
    }
  });

  const handleClick = (e) => {
    e.stopPropagation();
    setClicked(true);
    onSelect(skill);
    setTimeout(() => setClicked(false), 150);
  };

  return (
    <group
      ref={group}
      position={position}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={(e) => { e.stopPropagation(); setHovered(false); document.body.style.cursor = "none"; }}
      onPointerDown={handleClick}
    >
      <mesh position={[0, 0.4, 0]}>
        <boxGeometry args={[1.4, 0.6, 1.4]} />
        <meshPhysicalMaterial 
          color={hovered ? new THREE.Color(color).multiplyScalar(1.5) : new THREE.Color(color).multiplyScalar(0.7)} 
          roughness={0.1} 
          metalness={0.8}
          clearcoat={1.0}
          clearcoatRoughness={0.1}
          reflectivity={1}
        />
      </mesh>
      <Html 
        position={[0, 0.75, 0]} 
        center 
        style={{ pointerEvents: "none" }}
      >
        <div style={{
          width: "40px", height: "40px",
          display: "flex", justifyContent: "center", alignItems: "center",
          color: "#fff", fontWeight: "bold", fontSize: "14px",
          textShadow: "0 2px 4px rgba(0,0,0,0.8)",
          fontFamily: "'Inter', sans-serif"
        }}>
          {skill.image ? (
            <img src={skill.image} alt={skill.name} style={{ width: "30px", height: "30px", objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.8))" }} />
          ) : (
             skill.name.substring(0, 2).toUpperCase()
          )}
        </div>
      </Html>
    </group>
  );
};

const KeyboardLayout = ({ skills, onSelect }) => {
  const groupRef = useRef();
  
  const keys = useMemo(() => {
    const layout = [];
    const cols = 6;
    let r = 0;
    let c = 0;
    
    skills.forEach((skill, i) => {
      const xOffset = (r % 2 === 0) ? 0 : 0.75;
      const x = (c * 1.5 + xOffset) - ((cols * 1.5) / 2) + 0.75;
      const z = (r * 1.5) - ((5 * 1.5) / 2) + 0.75;
      
      layout.push({ id: skill.name, skill, position: [x, 0, z], color: colors[i % colors.length] });
      
      c++;
      if (c >= cols - (r % 2 === 0 ? 0 : 1)) {
        c = 0; r++;
      }
    });
    return layout;
  }, [skills]);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 1.5) * 0.1;
      groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.5) * 0.02;
    }
  });

  return (
    <group rotation={[Math.PI / 6, -Math.PI / 5, 0]}>
      <group ref={groupRef}>
        {/* Keyboard Base Case */}
        <mesh position={[0, -0.2, 0]}>
          <boxGeometry args={[11, 0.4, 9]} />
          <meshStandardMaterial color="#0b0d14" roughness={0.7} metalness={0.3} />
        </mesh>
        
        {/* Keys */}
        {keys.map((k) => (
          <Keycap key={k.id} position={k.position} skill={k.skill} color={k.color} onSelect={onSelect} />
        ))}
      </group>
    </group>
  );
};

const SkillsKeyboard = ({ skills, onSelect }) => {
  return (
    <div style={{ width: "100%", height: "100%", minHeight: "600px", position: "relative" }}>
      <Canvas shadows>
        <PerspectiveCamera makeDefault position={[0, 9, 12]} fov={40} />
        <OrbitControls enableZoom={false} enablePan={false} target={[0, 0, 0]} maxPolarAngle={Math.PI/2 - 0.2} minPolarAngle={Math.PI/4} />
        
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 20, 5]} intensity={1.5} castShadow />
        <pointLight position={[-10, 10, -10]} intensity={2.0} color="#00f0ff" />
        <pointLight position={[10, -10, 10]} intensity={1.5} color="#a855f7" />
        
        <KeyboardLayout skills={skills} onSelect={onSelect} />
        
        <Environment preset="city" />
      </Canvas>
    </div>
  );
};

export default SkillsKeyboard;
